import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Descriptions,
  Drawer,
  Dropdown,
  Form,
  Input,
  Modal,
  Select,
  Skeleton,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  MoreOutlined,
  PlusOutlined,
  ProfileOutlined,
  ReloadOutlined,
  SearchOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { capabilityApi, orgApi, userApi, workspaceApi } from '../api';
import { useAsync } from '../components/useAsync';
import PageShell from '../components/PageShell';
import DataTable from '../components/DataTable';
import GrantCapabilityModal from '../components/GrantCapabilityModal';
import MoveUserOrgModal from '../components/MoveUserOrgModal';
import { OrgRoleTag, StaffTag, StatusTag } from '../components/RoleTags';
import type {
  OrgRole,
  UserDetail,
  UserRow,
  WorkspaceRole,
} from '../types';
import {
  ORG_ROLE_LABEL,
  WORKSPACE_ROLE_LABEL,
  capabilityLabel,
} from '../types';

/**
 * What each access column means, once, where it is read.
 *
 * Two of them are spelled "admin", which is the whole confusion: the org role is
 * what the org-level gates read, and Seiki staff is internal elevation. They get
 * mistaken for one another constantly. A third kind — the workspace role — is
 * per workspace and so lives in the drawer, next to the memberships it belongs
 * to, where it is editable.
 */
const ORG_ROLE_HINT =
  "The user's position in their own company (users.org_role). Org admin is what opens creating workspaces, inviting people, and org settings — those gates read this column and nothing else.";

const STAFF_HINT =
  'Seiki employees operating inside a customer account: hard-deleting inventory and recalculating KPIs, in every org. Additive — it changes neither role to its left.';

/** A column heading that carries its own explanation. */
function ColumnLabel({ label, hint }: { label: string; hint: string }) {
  return (
    <Tooltip title={hint}>
      <span style={{ borderBottom: '1px dotted #9ca3af', cursor: 'help' }}>{label}</span>
    </Tooltip>
  );
}

export default function UsersPage() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [orgFilter, setOrgFilter] = useState<number | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  const [detailUserId, setDetailUserId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // The row being deleted, rather than just its id: the confirmation needs the
  // email to check what the operator types, and the row already carries it.
  const [deletingUser, setDeletingUser] = useState<UserRow | null>(null);
  // Same reason as above: the modal needs the current org name to say what the
  // move is *from*, and the row already carries it.
  const [movingUser, setMovingUser] = useState<UserRow | null>(null);

  const orgs = useAsync(() => orgApi.options(), []);

  const users = useAsync(
    () =>
      userApi.list({
        page,
        per_page: perPage,
        q: query || undefined,
        org_id: orgFilter,
        status: statusFilter,
      }),
    [page, perPage, query, orgFilter, statusFilter],
  );

  const orgOptions = useMemo(
    () => (orgs.data ?? []).map((o) => ({ value: o.id, label: o.name })),
    [orgs.data],
  );

  const applySearch = useCallback(() => {
    setPage(1);
    setQuery(search.trim());
  }, [search]);

  const columns: ColumnsType<UserRow> = [
    {
      title: 'User',
      key: 'user',
      render: (_, row) => (
        <div>
          <Button type="link" style={{ padding: 0 }} onClick={() => setDetailUserId(row.id)}>
            {row.first_name} {row.last_name}
          </Button>
          <div className="mono" style={{ color: '#6b7280' }}>
            {row.email}
          </div>
        </div>
      ),
    },
    {
      title: 'Organization',
      key: 'org',
      render: (_, row) =>
        row.org_name ? (
          <div>
            {row.org_name}
            {row.tenant_subdomain && (
              <div className="mono" style={{ color: '#6b7280' }}>
                {row.tenant_subdomain}
              </div>
            )}
          </div>
        ) : (
          // home_org_id is nullable until the org migration completes; a null
          // here is the reason super-admin scoping can silently degrade.
          <Tag color="orange">No org</Tag>
        ),
    },
    // Two access columns, not one "Role". `org_role` and `is_seiki_staff` are
    // what the gates actually read (plus the workspace membership) — so a single
    // summarized "account role" column would be the one least likely to explain
    // what someone could or could not do.
    {
      title: <ColumnLabel label="Org role" hint={ORG_ROLE_HINT} />,
      key: 'org_role',
      width: 160,
      render: (_, row) => <OrgRoleTag orgRole={row.org_role} />,
    },
    {
      title: <ColumnLabel label="Seiki staff" hint={STAFF_HINT} />,
      key: 'seiki_staff',
      width: 130,
      render: (_, row) => <StaffTag isSeikiStaff={row.is_seiki_staff} />,
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_, row) => <StatusTag status={row.status} isActive={row.is_active} />,
    },
    {
      title: 'Workspaces',
      dataIndex: 'workspace_count',
      key: 'workspace_count',
      width: 110,
      align: 'center',
    },
    {
      // Behind a menu rather than a button in the row: deleting is the only
      // irreversible action in this console, and it should not sit one stray
      // click away from the workspace count.
      title: '',
      key: 'actions',
      width: 56,
      align: 'center',
      render: (_, row) => (
        <Dropdown
          trigger={['click']}
          placement="bottomRight"
          menu={{
            items: [
              { key: 'open', icon: <ProfileOutlined />, label: 'Open details' },
              {
                key: 'move',
                icon: <SwapOutlined />,
                label: 'Move to another organization…',
              },
              { type: 'divider' },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Delete user…',
                danger: true,
              },
            ],
            onClick: ({ key }) => {
              if (key === 'open') setDetailUserId(row.id);
              if (key === 'move') setMovingUser(row);
              if (key === 'delete') setDeletingUser(row);
            },
          }}
        >
          <Button type="text" icon={<MoreOutlined />} aria-label={`Actions for ${row.email}`} />
        </Dropdown>
      ),
    },
  ];

  return (
    <PageShell
      title="Users"
      subtitle={
        <>
          Every user across every organization. Org role and Seiki staff are two
          separate facts &mdash; hover a heading for which gates it opens. Changes
          here take effect immediately.
        </>
      }
      actions={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={users.reload}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Create user
          </Button>
        </Space>
      }
      toolbar={
        <>
          <Input
            placeholder="Search email or name"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={applySearch}
            style={{ width: 260 }}
            allowClear
            onClear={() => {
              setSearch('');
              setQuery('');
              setPage(1);
            }}
          />
          <Button onClick={applySearch}>Search</Button>
          <Select
            placeholder="All organizations"
            style={{ width: 200 }}
            allowClear
            loading={orgs.loading}
            options={orgOptions}
            value={orgFilter}
            onChange={(v) => {
              setOrgFilter(v);
              setPage(1);
            }}
          />
          <Select
            placeholder="All statuses"
            style={{ width: 150 }}
            allowClear
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'pending', label: 'Pending' },
              { value: 'deactive', label: 'Deactivated' },
            ]}
          />
        </>
      }
    >
      <DataTable<UserRow>
        minWidth={1240}
        rowKey="id"
        loading={users.loading}
        columns={columns}
        dataSource={users.data?.items ?? []}
        pagination={{
          current: page,
          pageSize: perPage,
          total: users.data?.total ?? 0,
          showSizeChanger: true,
          showTotal: (total) => `${total} users`,
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPerPage(nextSize);
          },
        }}
      />

      <UserDetailDrawer
        userId={detailUserId}
        onClose={() => setDetailUserId(null)}
        onChanged={users.reload}
      />
      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={users.reload}
      />
      {movingUser && (
        <MoveUserOrgModal
          row={movingUser}
          onClose={() => setMovingUser(null)}
          onMoved={() => {
            setMovingUser(null);
            users.reload();
          }}
        />
      )}
      {deletingUser && (
        <DeleteUserModal
          row={deletingUser}
          onClose={() => setDeletingUser(null)}
          onDeleted={() => {
            setDeletingUser(null);
            // If the drawer happens to be open on the user just deleted, its
            // detail endpoint answers 404 from here on.
            if (detailUserId === deletingUser.id) setDetailUserId(null);
            users.reload();
          }}
        />
      )}
    </PageShell>
  );
}

function UserDetailDrawer({
  userId,
  onClose,
  onChanged,
}: {
  userId: number | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [saving, setSaving] = useState<string | null>(null);
  const [grantOpen, setGrantOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const detail = useAsync<UserDetail | null>(
    () => (userId ? userApi.get(userId) : Promise.resolve(null)),
    [userId],
  );
  const user = detail.data;

  // Live grants for this user, across every workspace they hold one in. Fetched
  // rather than read off the user row: a grant is scoped to a workspace and can
  // expire, neither of which a column on `users` could express.
  const grants = useAsync(
    () =>
      userId
        ? capabilityApi.grants({ user_id: userId, per_page: 100 })
        : Promise.resolve(null),
    [userId],
  );

  // Wraps every mutation so the drawer and the underlying table both refresh,
  // and a failed call leaves the previous value on screen rather than a
  // half-applied one.
  const mutate = useCallback(
    async (key: string, action: () => Promise<unknown>, successText: string) => {
      setSaving(key);
      try {
        await action();
        message.success(successText);
        await detail.reload();
        onChanged();
      } catch {
        /* toast already shown */
      } finally {
        setSaving(null);
      }
    },
    [detail, onChanged],
  );

  return (
    <Drawer
      title={user ? `${user.first_name} ${user.last_name}` : 'User'}
      width={620}
      open={userId !== null}
      onClose={onClose}
      destroyOnClose
      loading={detail.loading}
    >
      {user && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Email">
              <span className="mono">{user.email}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Organization">
              {user.org_name ?? <Tag color="orange">No org (home_org_id is null)</Tag>}
            </Descriptions.Item>
            {/* Both stored access facts on one line, in the same order and with
                the same tags as the table, so the drawer reads as the row the
                operator clicked rather than a second vocabulary. The derived
                account role is not among them: it is not a fact anyone here can
                set, and showing it invited exactly the confusion AD-15 removed. */}
            <Descriptions.Item label="Access">
              <Space size={4} wrap>
                <OrgRoleTag orgRole={user.org_role} />
                {user.is_seiki_staff && <Tag color="purple">Seiki staff</Tag>}
              </Space>
              <div>
                <Typography.Text type="secondary" className="mono" style={{ fontSize: 11 }}>
                  org_role={String(user.org_role)} ·
                  is_seiki_staff={String(user.is_seiki_staff)}
                </Typography.Text>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <StatusTag status={user.status} isActive={user.is_active} />
            </Descriptions.Item>
          </Descriptions>

          {/* Before the roles, because it is the outer scope: an org role means
              nothing until you know which org it is held in, and the wrong
              answer here is the reason someone gets deleted and recreated. */}
          <section>
            <Typography.Title level={5}>Organization</Typography.Title>
            <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
              Which customer this account belongs to. It decides the workspaces
              that resolve at all, the whitelabel they land on, and the product
              scope they read &mdash; so moving someone replaces their whole
              access, rather than adding to it.
            </Typography.Paragraph>
            <Space align="center" wrap>
              <Typography.Text strong>
                {user.org_name ?? <Tag color="orange">No org</Tag>}
              </Typography.Text>
              {user.tenant_subdomain && (
                <Typography.Text type="secondary" className="mono" style={{ fontSize: 12 }}>
                  {user.tenant_subdomain}
                </Typography.Text>
              )}
              <Button icon={<SwapOutlined />} onClick={() => setMoveOpen(true)}>
                Move to another organization…
              </Button>
            </Space>
            {/* Said here rather than only inside the modal: an operator
                deciding what to do about a misfiled account should see the
                answer without opening a dialog that then refuses. */}
            {!user.movable && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 10 }}
                message="This user cannot be moved"
                description={
                  <>
                    Records are keyed to this account rather than to a
                    workspace, so a move would carry them into the other
                    organization:{' '}
                    {(user.move_blockers ?? [])
                      .map((blocker) => `${blocker.label.toLowerCase()} (${blocker.count})`)
                      .join(', ')}
                    . Deactivate this account and create a new one in the
                    destination org instead.
                  </>
                }
              />
            )}
          </section>

          {/* One section rather than two, because the operator arrives asking
              "why can't they invite anyone?" and the answer is always which of
              these two is set. Both are stored and independent; neither is a
              summary of anything. */}
          <section>
            <Typography.Title level={5}>Roles</Typography.Title>
            <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
              The two access facts held on the user row. <strong>Org role</strong> is what
              the org-level gates read; <strong>Seiki staff</strong> is additive and leaves
              it untouched. A third kind, the workspace role, is per workspace &mdash;
              listed under Workspaces below, and owned by the Workspaces screen.
            </Typography.Paragraph>

            <div style={{ marginBottom: 20 }}>
              <Typography.Text strong>Org role</Typography.Text>
              <Typography.Paragraph
                type="secondary"
                style={{ fontSize: 12, margin: '2px 0 6px' }}
              >
                Their position in <strong>their own company</strong>.{' '}
                <strong>Org admin</strong> opens creating workspaces, inviting people to the
                org, and org settings &mdash; those gates read this column and nothing else.
              </Typography.Paragraph>
              {/* The case the old control hid: a null read as "Org member",
                  which grants nothing but looks deliberate. */}
              {!user.org_role && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message="Not set"
                  description="org_role is null, so nothing org-wide is open to this user. Pick a value to set it."
                />
              )}
              <Select<OrgRole>
                style={{ width: 220 }}
                placeholder="Not set"
                value={(user.org_role as OrgRole | null) ?? undefined}
                loading={saving === 'org_role'}
                onChange={(value) =>
                  mutate(
                    'org_role',
                    async () => {
                      const result = await userApi.setOrgRole(user.id, value);
                      // Without a home org, anything org-wide silently narrows to
                      // the user's own workspace — the warning set_seiki_staff.py
                      // (formerly a hand-run script) used to print.
                      if (value === 'org_admin' && result.home_org_id_missing) {
                        message.warning(
                          'Set, but this user has no home organization — org-wide scope will fall back to their own workspace until that is fixed.',
                          8,
                        );
                      }
                    },
                    'Org role updated',
                  )
                }
                options={(Object.keys(ORG_ROLE_LABEL) as OrgRole[]).map((value) => ({
                  value,
                  label: ORG_ROLE_LABEL[value],
                }))}
              />
            </div>

            <div>
              <Typography.Text strong>Seiki staff access</Typography.Text>
              <Typography.Paragraph
                type="secondary"
                style={{ fontSize: 12, margin: '2px 0 8px' }}
              >
                For Seiki employees who need to operate inside a customer&apos;s account:
                hard-deleting inventory and recalculating KPIs, across every organization.
              </Typography.Paragraph>
              <div style={{ display: 'flex', gap: 10 }}>
                <Switch
                  checked={user.is_seiki_staff}
                  loading={saving === 'seiki_staff'}
                  onChange={(checked) =>
                    mutate(
                      'seiki_staff',
                      () => userApi.setSeikiStaff(user.id, checked),
                      `Seiki staff access ${checked ? 'granted' : 'revoked'}`,
                    )
                  }
                />
                <div>
                  <div>Seiki staff</div>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Additive: it leaves the org role above exactly as it is. Only grant
                    this to Seiki employees &mdash; never to a customer.
                  </Typography.Text>
                </div>
              </div>
            </div>
          </section>

          <section>
            <Typography.Title level={5}>Account status</Typography.Title>
            <Select
              style={{ width: 220 }}
              value={user.status ?? (user.is_active ? 'active' : 'deactive')}
              loading={saving === 'status'}
              onChange={(value) =>
                mutate('status', () => userApi.setStatus(user.id, value), 'Status updated')
              }
              options={[
                { value: 'active', label: 'Active' },
                { value: 'pending', label: 'Pending' },
                { value: 'deactive', label: 'Deactivated' },
              ]}
            />
          </section>

          <section>
            <Typography.Title level={5}>Capability grants</Typography.Title>
            <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
              Access this user holds beyond their role, one workspace at a time.
              Usually the result of approving a request on Accesses; grant directly
              when the access was agreed elsewhere and no request exists to
              approve.
            </Typography.Paragraph>
            <Button
              icon={<PlusOutlined />}
              style={{ marginBottom: 12 }}
              disabled={user.workspaces.length === 0}
              onClick={() => setGrantOpen(true)}
            >
              Grant capability
            </Button>
            {(grants.data?.items ?? []).length === 0 ? (
              <Typography.Text type="secondary">
                {grants.loading
                  ? 'Loading…'
                  : 'No grants. This user works within their role.'}
              </Typography.Text>
            ) : (
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={grants.data?.items ?? []}
                columns={[
                  {
                    title: 'Capability',
                    dataIndex: 'capability',
                    render: (v: string) => capabilityLabel(v),
                  },
                  {
                    title: 'Workspace',
                    dataIndex: 'workspace_slug',
                    render: (v: string) => <span className="mono">{v}</span>,
                  },
                  {
                    title: 'Expires',
                    dataIndex: 'expires_at',
                    width: 120,
                    render: (v: string | null) =>
                      v ? (
                        dayjs(v).format('D MMM YYYY')
                      ) : (
                        <Typography.Text type="secondary">Never</Typography.Text>
                      ),
                  },
                ]}
              />
            )}
          </section>

          <section>
            <Typography.Title level={5}>Workspaces</Typography.Title>
            <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
              A role per workspace, independent of the two above: an org member can be
              admin of one workspace and a viewer in another. Editable here, because
              the question &ldquo;why can this person do that <em>there</em>?&rdquo;
              starts from the user. Adding and removing memberships still belongs to
              the Workspaces screen.
            </Typography.Paragraph>
            {user.workspaces.length === 0 ? (
              <Alert
                type="warning"
                showIcon
                message="No workspace membership"
                description="This user cannot see any inventory or campaigns until they belong to at least one workspace. Add them from the Workspaces screen."
              />
            ) : (
              <Table
                rowKey="workspace_id"
                size="small"
                pagination={false}
                dataSource={user.workspaces}
                columns={[
                  { title: 'Workspace', dataIndex: 'workspace_name' },
                  {
                    title: 'Slug',
                    dataIndex: 'workspace_slug',
                    render: (v: string) => <span className="mono">{v}</span>,
                  },
                  { title: 'Country', dataIndex: 'country', width: 80 },
                  {
                    title: 'Workspace role',
                    key: 'role',
                    width: 170,
                    render: (_, row) => (
                      <Select<WorkspaceRole>
                        size="small"
                        style={{ width: 150 }}
                        value={row.role}
                        loading={saving === `ws-role:${row.workspace_id}`}
                        onChange={(value) =>
                          mutate(
                            `ws-role:${row.workspace_id}`,
                            () =>
                              workspaceApi.updateMemberRole(
                                row.workspace_id,
                                user.id,
                                value,
                              ),
                            `Role in ${row.workspace_name} updated`,
                          )
                        }
                        options={(Object.keys(WORKSPACE_ROLE_LABEL) as WorkspaceRole[]).map(
                          (value) => ({ value, label: WORKSPACE_ROLE_LABEL[value] }),
                        )}
                      />
                    ),
                  },
                ]}
              />
            )}
          </section>

          <section>
            <Typography.Title level={5}>Password</Typography.Title>
            <Button
              loading={saving === 'reset'}
              onClick={() =>
                mutate(
                  'reset',
                  async () => {
                    const result = await userApi.sendPasswordReset(user.id);
                    if (!result.sent) {
                      message.warning(
                        'The reset email could not be sent. Ask the user to use "Forgot password" on the login page instead.',
                        8,
                      );
                    }
                  },
                  'Password reset email sent',
                )
              }
            >
              Send password reset email
            </Button>
          </section>

          {/* Mounted only while open, so each grant starts on an empty form.
              The user is fixed here and their memberships are already loaded,
              which is exactly what a grant needs. */}
          {grantOpen && (
            <GrantCapabilityModal
              subject={user}
              onClose={() => setGrantOpen(false)}
              onGranted={grants.reload}
            />
          )}
          {moveOpen && (
            <MoveUserOrgModal
              row={user}
              onClose={() => setMoveOpen(false)}
              onMoved={async () => {
                setMoveOpen(false);
                // Everything on this panel is scoped to the old org — the
                // memberships and the grants most of all — so both reload.
                await Promise.all([detail.reload(), grants.reload()]);
                onChanged();
              }}
            />
          )}
        </Space>
      )}
    </Drawer>
  );
}

/**
 * Confirmation for the only irreversible action in this console.
 *
 * Opened from a row's menu, which means it starts without knowing whether this
 * user can be deleted at all: `deletable` and the blocker counts come from the
 * detail endpoint, and computing them for every row of every page would cost
 * ten count queries a row. So the preflight happens here, once, at the point an
 * operator has actually reached for the action — and the modal has two faces
 * depending on the answer.
 *
 * The address has to be typed rather than a checkbox ticked, and what gets
 * typed is what gets sent: the backend rejects a mismatch, so the ceremony is a
 * real check end to end and not just a speed bump in the browser.
 */
function DeleteUserModal({
  row,
  onClose,
  onDeleted,
}: {
  row: UserRow;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const detail = useAsync<UserDetail | null>(() => userApi.get(row.id), [row.id]);
  const user = detail.data;
  const blocked = !!user && !user.deletable;
  const matches = typed.trim().toLowerCase() === row.email.toLowerCase();

  async function onSubmit() {
    setSubmitting(true);
    setRefusal(null);
    try {
      const result = await userApi.remove(row.id, typed.trim());
      message.success(`${result.email} deleted.`);
      onDeleted();
    } catch (error) {
      // Rendered here rather than toasted (the call is silent): a refusal is a
      // paragraph about what to do instead, and it belongs next to the button
      // that was just refused.
      setRefusal(error instanceof Error ? error.message : 'The user was not deleted.');
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      title={blocked ? 'This user cannot be deleted' : 'Delete this user?'}
      onCancel={onClose}
      okText="Delete permanently"
      okButtonProps={{
        danger: true,
        disabled: !user?.deletable || !matches,
        loading: submitting,
      }}
      onOk={onSubmit}
      // Nothing to confirm when the answer is already no, so the modal drops to
      // a single way out rather than offering a button that cannot fire.
      footer={blocked ? <Button onClick={onClose}>Close</Button> : undefined}
      destroyOnClose
    >
      {detail.loading || !user ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
            Deactivating a user blocks sign-in and keeps a record of what they did,
            which is almost always the right move. Deleting removes the row and
            cannot be undone &mdash; it is for accounts that should never have
            existed (a mistyped address, a duplicate, the wrong org) or for an
            erasure request.
          </Typography.Paragraph>

          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="User">
              {user.first_name} {user.last_name}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              <span className="mono">{user.email}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Organization">
              {user.org_name ?? <Tag color="orange">No org</Tag>}
            </Descriptions.Item>
          </Descriptions>

          {blocked ? (
            // Not an error and not a permission problem — the account simply has
            // history, and the counts are the useful part of saying so.
            <Alert
              type="info"
              showIcon
              message="Records elsewhere name this user"
              description={
                <>
                  <div>
                    Deleting the row would either orphan them or take a
                    customer&apos;s work with it:
                  </div>
                  <ul style={{ margin: '8px 0 8px 18px', padding: 0 }}>
                    {/* Defaulted, not asserted: if this console ever ships ahead
                        of the backend that added the field, an absent list should
                        read as "no detail" rather than crash the modal. */}
                    {(user.deletion_blockers ?? []).map((blocker) => (
                      <li key={blocker.kind}>
                        {blocker.label} &mdash; <strong>{blocker.count}</strong>
                      </li>
                    ))}
                  </ul>
                  <div>
                    Set their status to <strong>Deactivated</strong> instead, from
                    Open details: they cannot sign in, and their work keeps its
                    author.
                  </div>
                </>
              }
            />
          ) : (
            <>
              <Typography.Paragraph style={{ margin: 0 }}>
                Nothing outside this account points at it, so it can go. Deleted with
                it: their sessions, {user.workspaces.length} workspace membership
                {user.workspaces.length === 1 ? '' : 's'}, and any capability grants
                or pending requests.
              </Typography.Paragraph>
              {user.is_seiki_staff && (
                <Alert
                  type="warning"
                  showIcon
                  message="This is a Seiki staff account"
                  description="Check with whoever operates it before deleting — staff accounts are usually ours, not a customer's."
                />
              )}
              <div>
                <Typography.Text>
                  Type <span className="mono">{user.email}</span> to confirm.
                </Typography.Text>
                <Input
                  autoFocus
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  onPressEnter={() => matches && !submitting && onSubmit()}
                  placeholder={user.email}
                  style={{ marginTop: 6 }}
                />
              </div>
              {refusal && (
                <Alert type="error" showIcon message="Not deleted" description={refusal} />
              )}
            </>
          )}
        </Space>
      )}
    </Modal>
  );
}

function CreateUserModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [orgId, setOrgId] = useState<number | undefined>();

  const orgs = useAsync(() => orgApi.options(), []);

  // Workspace choices depend on the selected org — a user may only join
  // workspaces their own org owns, which the API enforces too.
  const workspaces = useAsync(
    () =>
      orgId
        ? workspaceApi.list({ org_id: orgId, per_page: 100 })
        : Promise.resolve({ items: [], total: 0 }),
    [orgId],
  );

  async function onSubmit() {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      const result = await userApi.create({
        email: values.email.trim(),
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        org_id: values.org_id,
        org_role: values.org_role,
        workspace_ids: values.workspace_ids ?? [],
        workspace_role: values.workspace_role,
        send_password_reset: values.send_password_reset ?? true,
      });
      message.success(`Created ${result.email}`);
      if (values.send_password_reset && !result.password_reset_link_sent) {
        message.warning(
          'User created, but the welcome email could not be sent. Send a password reset from their detail panel.',
          8,
        );
      }
      form.resetFields();
      setOrgId(undefined);
      onCreated();
      onClose();
    } catch {
      /* validation errors and toasts already handled */
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Create user"
      open={open}
      onCancel={onClose}
      onOk={onSubmit}
      okText="Create"
      confirmLoading={submitting}
      width={560}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="No password is set here"
        description="The user receives a reset link and chooses their own password."
      />
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        // The two role fields are picked directly, because they are what
        // create_user stores — since AD-15 it ignores an account-role argument
        // entirely. Keep the defaults consistent with each other: defaulting
        // them apart (admin + org_member, as this form once did) created users
        // who read as admins and could not invite anyone.
        initialValues={{
          org_role: 'org_admin',
          workspace_role: 'workspace_admin',
          send_password_reset: true,
        }}
      >
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Email is required' },
            { type: 'email', message: 'Enter a valid email address' },
          ]}
        >
          <Input placeholder="person@customer.com" />
        </Form.Item>
        <Space style={{ display: 'flex' }} align="start">
          <Form.Item
            name="first_name"
            label="First name"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="last_name"
            label="Last name"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input />
          </Form.Item>
        </Space>
        <Form.Item
          name="org_id"
          label="Organization"
          rules={[{ required: true, message: 'Pick an organization' }]}
        >
          <Select
            loading={orgs.loading}
            placeholder="Select organization"
            onChange={(value: number) => {
              setOrgId(value);
              form.setFieldValue('workspace_ids', []);
            }}
            options={(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name }))}
          />
        </Form.Item>
        <Form.Item
          name="org_role"
          label="Org role"
          extra="Their position in their own company: org admin can create workspaces, invite people, and edit org settings. Seiki staff access is separate, granted afterwards from the user's detail panel."
        >
          <Select
            options={(Object.keys(ORG_ROLE_LABEL) as OrgRole[]).map((value) => ({
              value,
              label: ORG_ROLE_LABEL[value],
            }))}
          />
        </Form.Item>
        <Form.Item
          name="workspace_ids"
          label="Workspaces"
          extra="Leave empty to add them to the organization's default workspace."
        >
          <Select
            mode="multiple"
            disabled={!orgId}
            loading={workspaces.loading}
            placeholder={orgId ? 'Default workspace' : 'Select an organization first'}
            options={(workspaces.data?.items ?? []).map((w) => ({
              value: w.id,
              label: `${w.name}${w.country ? ` (${w.country})` : ''}`,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="workspace_role"
          label="Workspace role"
          extra="Applies in each workspace picked above, and nowhere else."
        >
          <Select
            options={(Object.keys(WORKSPACE_ROLE_LABEL) as WorkspaceRole[]).map((value) => ({
              value,
              label: WORKSPACE_ROLE_LABEL[value],
            }))}
          />
        </Form.Item>
        <Form.Item name="send_password_reset" valuePropName="checked">
          <Checkbox>Send the welcome / password-reset email now</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
}
